import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Activity, AlertTriangle, CheckCircle2, Coins, Download, FileText,
    Info, Loader2, Package, Palette, PlusCircle, RefreshCw, Sparkles,
    Tag, Trophy, Upload, Wallet, X,
} from "lucide-react";
import { toast } from "sonner";

import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { TierProgressCard } from "@/components/TierProgressCard";
import { CreatorEarningsWidget } from "@/components/marketplace/CreatorEarningsWidget";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";


function KpiCard({ icon: Icon, label, value, hint, tone = "primary", testid }) {
    const toneCls = {
        primary: "text-[var(--color-primary)]",
        secondary: "text-[var(--color-secondary)]",
        success: "text-[var(--color-success)]",
        warning: "text-neutral-400",
        alert: "text-[var(--color-alert)]",
    }[tone];
    return (
        <div
            data-testid={testid}
            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4"
        >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-2">
                <Icon className={`h-3 w-3 ${toneCls}`} /> {label}
            </div>
            <div className={`text-2xl font-semibold tabular-nums ${toneCls}`}>{value}</div>
            {hint && <p className="text-[10px] text-text-secondary mt-1 leading-tight">{hint}</p>}
        </div>
    );
}


function StatusBadge({ status }) {
    const cls = {
        pending_review: "text-neutral-400 bg-neutral-400/10 border-neutral-400/30",
        changes_requested: "text-neutral-400 bg-neutral-400/10 border-neutral-400/30",
        published: "text-[var(--color-success)] bg-[var(--color-success)]/10 border-[var(--color-success)]/40",
        rejected: "text-[var(--color-alert)] bg-[var(--color-alert)]/10 border-[var(--color-alert)]/40",
    }[status] || "text-text-secondary bg-white/5 border-white/10";
    return (
        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${cls}`}>
            {(status || "").replace(/_/g, " ")}
        </span>
    );
}


function TabBtn({ active, onClick, icon: Icon, label, count, testid }) {
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors flex items-center gap-2 ${
                active
                    ? "border-[var(--color-primary)] text-text-main bg-[var(--color-primary)]/10"
                    : "border-[var(--color-border)] text-text-secondary hover:text-text-main"
            }`}
        >
            <Icon className="h-3 w-3" /> {label}
            {typeof count === "number" && (
                <span className="text-[10px] tabular-nums opacity-70">({count})</span>
            )}
        </button>
    );
}


const ASSET_TYPES = [
    { value: "template", label: "Template" },
    { value: "module", label: "Module" },
    { value: "pack", label: "Pack" },
    { value: "service", label: "Service" },
];


function UploadModal({ open, onClose, onDone }) {
    const [file, setFile] = useState(null);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [category, setCategory] = useState("outreach");
    const [assetType, setAssetType] = useState("template");
    const [price, setPrice] = useState(100);
    const [tags, setTags] = useState("");
    const [busy, setBusy] = useState(false);

    const reset = () => {
        setFile(null); setTitle(""); setDescription("");
        setCategory("outreach"); setAssetType("template");
        setPrice(100); setTags("");
    };

    const submit = async () => {
        if (!file) { toast.error("Please pick a file"); return; }
        if (!title.trim() || !description.trim()) {
            toast.error("Title & description are required"); return;
        }
        setBusy(true);
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", title.trim());
        fd.append("description", description.trim());
        fd.append("category", category.trim() || "general");
        fd.append("asset_type", assetType);
        fd.append("price_credits", String(price));
        fd.append("tags", tags);
        try {
            const r = await api.mp3UploadAsset(fd);
            toast.success(`Asset uploaded · v${r.version} · awaiting review`);
            reset();
            onDone && onDone(r);
            onClose && onClose();
        } catch (e) {
            const detail = e?.data?.detail;
            if (e.status === 413) {
                toast.error(`File too large (${(detail?.size_bytes / 1024 / 1024).toFixed(2)}MB > ${detail?.max_mb}MB max)`);
            } else if (e.status === 415) {
                toast.error(`MIME type not allowed: ${detail?.content_type}`);
            } else {
                toast.error(e.message || "Upload failed");
            }
        } finally { setBusy(false); }
    };

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
                        onClick={busy ? undefined : onClose}
                    />
                    <motion.div
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
                    >
                        <div
                            data-testid="mp3-upload-modal"
                            className="pointer-events-auto w-full max-w-lg rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
                        >
                            <div className="flex items-start justify-between px-5 py-4 border-b border-[var(--color-border)]">
                                <div>
                                    <h3 className="text-base font-semibold">Upload asset</h3>
                                    <p className="text-xs text-text-secondary mt-1">Max 15MB · JSON, YAML, ZIP, PDF, PNG, JPG, MD, TXT · Auto-status: pending review</p>
                                </div>
                                <button onClick={onClose} disabled={busy} className="text-text-secondary hover:text-text-main disabled:opacity-40">
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                            <div className="p-5 overflow-y-auto flex-1 space-y-3">
                                <label className="block text-xs text-text-secondary">
                                    File
                                    <input
                                        data-testid="mp3-upload-file"
                                        type="file"
                                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                                        className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm text-text-main file:bg-[var(--color-primary)] file:text-white file:border-0 file:mr-3 file:px-3 file:py-1 file:rounded-md file:text-xs"
                                    />
                                    {file && (
                                        <span className="text-[10px] text-text-secondary mt-1 block">
                                            {file.name} · {(file.size / 1024).toFixed(1)}KB · {file.type || "unknown"}
                                        </span>
                                    )}
                                </label>
                                <label className="block text-xs text-text-secondary">
                                    Title
                                    <input
                                        data-testid="mp3-upload-title"
                                        type="text" value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        placeholder="Cold email 5-step SaaS pack"
                                        className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm text-text-main"
                                    />
                                </label>
                                <label className="block text-xs text-text-secondary">
                                    Description
                                    <textarea
                                        data-testid="mp3-upload-description"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        rows={3}
                                        placeholder="Battle-tested 5-step sequence for SaaS founders …"
                                        className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm text-text-main"
                                    />
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                    <label className="block text-xs text-text-secondary">
                                        Asset type
                                        <select
                                            data-testid="mp3-upload-type"
                                            value={assetType}
                                            onChange={(e) => setAssetType(e.target.value)}
                                            className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm text-text-main"
                                        >
                                            {ASSET_TYPES.map((t) => (
                                                <option key={t.value} value={t.value}>{t.label}</option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="block text-xs text-text-secondary">
                                        Category
                                        <input
                                            type="text" value={category}
                                            onChange={(e) => setCategory(e.target.value)}
                                            placeholder="outreach"
                                            className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm text-text-main"
                                        />
                                    </label>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <label className="block text-xs text-text-secondary">
                                        Price (credits)
                                        <input
                                            data-testid="mp3-upload-price"
                                            type="number" min={0} value={price}
                                            onChange={(e) => setPrice(Math.max(0, Number(e.target.value)))}
                                            className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm text-text-main"
                                        />
                                    </label>
                                    <label className="block text-xs text-text-secondary">
                                        Tags <span className="opacity-60">(comma-separated)</span>
                                        <input
                                            type="text" value={tags}
                                            onChange={(e) => setTags(e.target.value)}
                                            placeholder="saas, cold-email, high-converting"
                                            className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm text-text-main"
                                        />
                                    </label>
                                </div>
                            </div>
                            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[var(--color-border)] bg-[var(--color-bg)]/40">
                                <button onClick={onClose} disabled={busy} className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-4 py-2 disabled:opacity-40">Cancel</button>
                                <button
                                    onClick={submit} disabled={busy || !file}
                                    data-testid="mp3-upload-submit"
                                    className="text-xs rounded-full px-5 py-2 font-medium flex items-center gap-2 bg-white hover:bg-[#EDEDED] text-black disabled:opacity-40"
                                >
                                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                                    Upload
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}


function fmtEur(v) { return `$${Number(v || 0).toFixed(2)}`; }
function fmtCredits(v) { return `${Number(v || 0).toLocaleString()} cr`; }
function fmtDate(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleString(); } catch { return iso; } }


export default function CreatorDashboardV3() {
    const { user: me } = useAuth();
    const [tab, setTab] = useState("assets");
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [showUpload, setShowUpload] = useState(false);

    const [kpi, setKpi] = useState({});
    const [assets, setAssets] = useState([]);
    const [revenue, setRevenue] = useState({});

    const load = useCallback(async () => {
        try {
            const [k, a, r] = await Promise.all([
                api.mp3CreatorDashboard(),
                api.mp3CreatorAssets(100),
                api.mp3CreatorRevenue(),
            ]);
            setKpi(k || {});
            setAssets(a?.items || []);
            setRevenue(r || {});
            setDisabled(false);
        } catch (e) {
            if (e.status === 503) setDisabled(true);
            else toast.error(e.message || "Failed to load");
        }
    }, []);

    useEffect(() => {
        (async () => { setLoading(true); await load(); setLoading(false); })();
    }, [load]);

    const refresh = async () => {
        setRefreshing(true); await load(); setRefreshing(false);
    };

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar />
            <main className="flex-1 flex flex-col min-w-0">
                <Header />
                <div className="flex-1 p-6 md:p-8 space-y-6 max-w-[1200px] w-full mx-auto">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                        <div>
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-1">
                                <Palette className="h-3 w-3 text-[var(--color-secondary)]" />
                                Creator Studio · Marketplace v3 · Phase 16c
                            </div>
                            <h1
                                data-testid="mp3-creator-title"
                                className="text-2xl md:text-3xl font-semibold tracking-tight"
                            >
                                Creator studio
                            </h1>
                            <p className="text-xs text-text-secondary mt-1 max-w-xl">
                                Publish templates, modules, packs & services. Revenue share <span className="text-text-main">70/30</span> · Min payout <span className="text-text-main">500 credits</span> · Moderation manual.
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                data-testid="mp3-creator-refresh"
                                onClick={refresh} disabled={refreshing || loading}
                                className="text-xs rounded-full border border-[var(--color-border)] px-3 py-1.5 text-text-secondary hover:text-text-main disabled:opacity-40 flex items-center gap-2"
                            >
                                {refreshing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                                Refresh
                            </button>
                            <button
                                data-testid="mp3-creator-upload-btn"
                                onClick={() => setShowUpload(true)}
                                disabled={disabled}
                                className="text-xs rounded-full px-4 py-1.5 font-medium flex items-center gap-2 bg-white hover:bg-[#EDEDED] text-black disabled:opacity-40"
                            >
                                <Upload className="h-3 w-3" /> Upload asset
                            </button>
                        </div>
                    </div>

                    {disabled ? (
                        <div
                            data-testid="mp3-creator-disabled-banner"
                            className="rounded-2xl border border-neutral-400/30 bg-neutral-400/5 p-6 flex items-start gap-3"
                        >
                            <AlertTriangle className="h-5 w-5 text-neutral-400 shrink-0 mt-0.5" />
                            <div>
                                <h3 className="text-sm font-semibold text-neutral-400">Marketplace v3 is temporarily disabled</h3>
                                <p className="text-xs text-text-secondary mt-1">The creator studio is currently paused by admin. Your existing assets remain unchanged.</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                <KpiCard icon={Package} label="Assets" value={kpi.my_assets_count ?? 0} tone="primary" testid="mp3-kpi-assets" />
                                <KpiCard icon={CheckCircle2} label="Published" value={kpi.published_count ?? 0} tone="success" testid="mp3-kpi-published" />
                                <KpiCard icon={FileText} label="In review" value={kpi.pending_review_count ?? 0} tone="warning" testid="mp3-kpi-pending" />
                                <KpiCard icon={Download} label="Downloads" value={kpi.total_downloads ?? 0} tone="secondary" testid="mp3-kpi-downloads" />
                                <KpiCard icon={Wallet} label="Pending payout" value={fmtCredits(kpi.pending_creator_credits)} hint={`≈ ${fmtEur((kpi.pending_creator_credits || 0) / 100)}`} tone="primary" testid="mp3-kpi-payout" />
                            </div>

                            {/* Phase 17e · Verified Creator tier progress */}
                            <TierProgressCard kind="verified_creator" />
                            {/* Phase 24e · V4 Creator earnings snapshot (paid tier) */}
                            <CreatorEarningsWidget />

                            <div className="flex flex-wrap items-center gap-2">
                                <TabBtn active={tab === "assets"} onClick={() => setTab("assets")} icon={Package} label="My assets" count={assets.length} testid="mp3-tab-assets" />
                                <TabBtn active={tab === "revenue"} onClick={() => setTab("revenue")} icon={Coins} label="Revenue" count={revenue.total_purchases || 0} testid="mp3-tab-revenue" />
                            </div>

                            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
                                {loading ? (
                                    <div className="p-10 flex items-center justify-center text-text-secondary text-xs gap-2">
                                        <Loader2 className="h-3 w-3 animate-spin" /> Loading…
                                    </div>
                                ) : tab === "assets" ? (
                                    <AssetsTable rows={assets} onRefresh={refresh} />
                                ) : (
                                    <RevenueTable data={revenue} />
                                )}
                            </div>

                            <div className="text-[10px] text-text-secondary flex items-start gap-2 max-w-xl">
                                <Info className="h-3 w-3 mt-0.5 shrink-0" />
                                <span>All uploads (initial and versions) go through manual admin moderation. Approved assets automatically get 1 SEO suggestion + a creator email notification (pending admin dispatch).</span>
                            </div>
                        </>
                    )}
                </div>
            </main>

            <UploadModal open={showUpload} onClose={() => setShowUpload(false)} onDone={refresh} />
        </div>
    );
}


function AssetsTable({ rows, onRefresh }) {
    const [uploadForId, setUploadForId] = useState(null);
    if (!rows.length) {
        return (
            <div className="p-10 text-center text-xs text-text-secondary">
                You haven't uploaded any asset yet. Click <span className="text-text-main">Upload asset</span> to get started.
            </div>
        );
    }
    return (
        <>
            <div className="overflow-x-auto">
                <table data-testid="mp3-assets-table" className="w-full text-xs">
                    <thead className="border-b border-[var(--color-border)] text-text-secondary uppercase tracking-widest text-[10px]">
                        <tr>
                            <th className="text-left px-4 py-3">Title</th>
                            <th className="text-left px-4 py-3">Type</th>
                            <th className="text-right px-4 py-3">Price</th>
                            <th className="text-right px-4 py-3">Downloads</th>
                            <th className="text-right px-4 py-3">Version</th>
                            <th className="text-left px-4 py-3">Status</th>
                            <th className="text-right px-4 py-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--color-border)]">
                        {rows.map((r) => (
                            <tr key={r.id} className="hover:bg-white/[0.02]">
                                <td className="px-4 py-3 text-text-main truncate max-w-[240px]">{r.title}</td>
                                <td className="px-4 py-3 text-text-secondary">{r.asset_type}</td>
                                <td className="px-4 py-3 text-right tabular-nums text-[var(--color-secondary)]">{fmtCredits(r.price_credits)}</td>
                                <td className="px-4 py-3 text-right tabular-nums text-text-secondary">{r.download_count ?? 0}</td>
                                <td className="px-4 py-3 text-right font-mono text-text-secondary">v{r.current_version}</td>
                                <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                                <td className="px-4 py-3 text-right">
                                    <button
                                        onClick={() => setUploadForId(r.id)}
                                        data-testid={`mp3-asset-version-${r.slug}`}
                                        className="text-[10px] rounded-full border border-[var(--color-border)] px-2 py-1 text-text-secondary hover:text-text-main"
                                    >
                                        + version
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <VersionModal open={!!uploadForId} assetId={uploadForId} onClose={() => setUploadForId(null)} onDone={onRefresh} />
        </>
    );
}


function VersionModal({ open, assetId, onClose, onDone }) {
    const [file, setFile] = useState(null);
    const [bump, setBump] = useState("patch");
    const [changelog, setChangelog] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!file) { toast.error("Pick a file"); return; }
        setBusy(true);
        const fd = new FormData();
        fd.append("file", file);
        fd.append("version_bump", bump);
        fd.append("changelog", changelog);
        try {
            const r = await api.mp3AddVersion(assetId, fd);
            toast.success(`New version v${r.new_version} · awaiting review`);
            setFile(null); setChangelog(""); setBump("patch");
            onDone && onDone();
            onClose && onClose();
        } catch (e) {
            toast.error(e.message || "Version upload failed");
        } finally { setBusy(false); }
    };

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm" onClick={busy ? undefined : onClose} />
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
                        <div data-testid="mp3-version-modal" className="pointer-events-auto w-full max-w-md rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] shadow-2xl overflow-hidden">
                            <div className="flex items-start justify-between px-5 py-4 border-b border-[var(--color-border)]">
                                <div>
                                    <h3 className="text-base font-semibold">Upload new version</h3>
                                    <p className="text-xs text-text-secondary mt-1">Auto-bumps semver · Resets status to pending review</p>
                                </div>
                                <button onClick={onClose} disabled={busy} className="text-text-secondary hover:text-text-main disabled:opacity-40"><X className="h-4 w-4" /></button>
                            </div>
                            <div className="p-5 space-y-3">
                                <label className="block text-xs text-text-secondary">
                                    New file
                                    <input data-testid="mp3-version-file" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm text-text-main file:bg-[var(--color-primary)] file:text-white file:border-0 file:mr-3 file:px-3 file:py-1 file:rounded-md file:text-xs" />
                                </label>
                                <label className="block text-xs text-text-secondary">
                                    Version bump
                                    <select data-testid="mp3-version-bump" value={bump} onChange={(e) => setBump(e.target.value)} className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm text-text-main">
                                        <option value="patch">patch — bug fixes (1.0.0 → 1.0.1)</option>
                                        <option value="minor">minor — new features (1.0.0 → 1.1.0)</option>
                                        <option value="major">major — breaking (1.0.0 → 2.0.0)</option>
                                    </select>
                                </label>
                                <label className="block text-xs text-text-secondary">
                                    Changelog
                                    <textarea data-testid="mp3-version-changelog" value={changelog} onChange={(e) => setChangelog(e.target.value)} rows={2} placeholder="What changed in this version…" className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm text-text-main" />
                                </label>
                            </div>
                            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[var(--color-border)] bg-[var(--color-bg)]/40">
                                <button onClick={onClose} disabled={busy} className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-4 py-2 disabled:opacity-40">Cancel</button>
                                <button onClick={submit} disabled={busy || !file} data-testid="mp3-version-submit" className="text-xs rounded-full px-5 py-2 font-medium flex items-center gap-2 bg-white hover:bg-[#EDEDED] text-black disabled:opacity-40">
                                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                                    Upload version
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}


function RevenueTable({ data }) {
    const rows = data?.per_asset || [];
    if (!rows.length) {
        return <div className="p-10 text-center text-xs text-text-secondary">No purchases yet — share your marketplace URL to start earning.</div>;
    }
    return (
        <div className="overflow-x-auto">
            <table data-testid="mp3-revenue-table" className="w-full text-xs">
                <thead className="border-b border-[var(--color-border)] text-text-secondary uppercase tracking-widest text-[10px]">
                    <tr>
                        <th className="text-left px-4 py-3">Asset</th>
                        <th className="text-right px-4 py-3">Purchases</th>
                        <th className="text-right px-4 py-3">Creator credits</th>
                        <th className="text-right px-4 py-3">≈ EUR</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border)]">
                    {rows.map((r) => (
                        <tr key={r.asset_id} className="hover:bg-white/[0.02]">
                            <td className="px-4 py-3 text-text-main">{r.asset_title || r.asset_id}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-text-secondary">{r.purchases}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-[var(--color-secondary)]">{fmtCredits(r.creator_credits)}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-[var(--color-success)]">{fmtEur(r.creator_credits / 100)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
